/**
 * Modèle interne du document roadbook.
 *
 * Il est **distinct du format d'échange** de l'annexe A : le premier est
 * optimisé pour le calcul, le second pour la stabilité et la relecture. La
 * conversion se fait dans `io/`, en un seul endroit.
 *
 * Le document est `readonly` de bout en bout. Une modification produit un
 * nouvel objet pour la branche touchée et réutilise le reste. Cela sert la
 * détection de changement par identité, l'empreinte de fraîcheur de EF-CAL-005
 * et la fenêtre d'annulation de la purge de EF-POI-016.
 */

import type { Coordinate } from '../geo/geodesy';
import type { ComputedTrack } from '../geo/track';
import type { CategoryKey } from './categories';
import type { LocalDateTime } from './local-datetime';

export type PoiId = string;

export type PoiOrigin = 'osm' | 'manual';

export type OsmType = 'node' | 'way' | 'relation';

export interface OsmRef {
  readonly type: OsmType;
  readonly id: number;
  readonly matchedTags: Readonly<Record<string, string>>;
}

/** Clé fonctionnelle de déduplication (EF-POI-010, EF-POI-015). */
export function osmKey(ref: OsmRef): string {
  return `${ref.type}/${ref.id}`;
}

/** État de présence dans le dernier résultat — EF-POI-005. */
export type PresenceState = 'present' | 'absentFromResult' | 'rejectedByFilter' | 'rangeNotCovered';

export type ManualInputMode = 'coordinates' | 'progression' | 'currentPosition';

export interface PoiUserState {
  readonly excluded: boolean;
  readonly pinned: boolean;
  /** Au plus 500 points de code après normalisation NFC. */
  readonly note: string | null;
  readonly presenceState: PresenceState;
  /** Cause issue de EF-POI-013 lorsque `presenceState` vaut `rejectedByFilter`. */
  readonly rejectionCause: string | null;
  readonly outOfSearchedPortion: boolean;
  readonly categoriesChanged: boolean;
}

export interface PassageComputed {
  readonly distanceFromStartMeters: number;
  readonly distanceToFinishMeters: number;
  readonly elevationGainFromStartMeters: number | null;
  readonly elevationGainToFinishMeters: number | null;
  readonly estimatedArrivalLocal: LocalDateTime;
  /** Par rapport à la ligne précédente non exclue ; `null` pour la première. */
  readonly distanceFromPreviousMeters: number | null;
  readonly elevationGainFromPreviousMeters: number | null;
  readonly durationFromPreviousSeconds: number | null;
}

export interface Passage {
  /** Rang du passage, à partir de 1, par progression croissante. */
  readonly index: number;
  readonly progressionMeters: number;
  readonly distanceToRouteMeters: number;
  readonly computed: PassageComputed | null;
}

export interface Poi {
  readonly id: PoiId;
  readonly origin: PoiOrigin;
  readonly osm: OsmRef | null;
  /** Non vide, ordonnée par rang de EF-POI-001. */
  readonly categories: readonly CategoryKey[];
  readonly name: string | null;
  readonly openingHours: string | null;
  readonly phone: string | null;
  readonly email: string | null;
  readonly website: string | null;
  readonly accessNote: string | null;
  readonly coordinate: Coordinate;
  /** Portée de passage : corridor en vigueur à la création ou dernière fusion. */
  readonly passageReachMeters: number;
  /** Au moins un élément, ordonné par progression croissante. */
  readonly passages: readonly Passage[];
  readonly manualInput: ManualInputMode | null;
  readonly userState: PoiUserState;
}

export interface SelectedElement {
  readonly type: 'trk' | 'rte';
  readonly name: string | null;
  readonly index: number;
}

export interface RouteData {
  readonly sourceFileName: string | null;
  readonly selectedElement: SelectedElement;
  readonly originalPointCount: number;
  readonly track: ComputedTrack;
}

export type StopType = 'start' | 'pause' | 'sleep' | 'finish';

export interface Stop {
  readonly type: StopType;
  readonly startLocal: LocalDateTime;
  readonly endLocal: LocalDateTime | null;
  readonly progressionMeters: number;
  readonly durationSeconds: number | null;
  readonly nightIndex: number | null;
}

export interface Totals {
  readonly distanceMeters: number;
  readonly elevationGainMeters: number | null;
  readonly ridingSeconds: number;
  readonly pauseSeconds: number;
  readonly sleepSeconds: number;
  readonly totalSeconds: number;
  readonly startLocal: LocalDateTime;
  readonly finishLocal: LocalDateTime;
  readonly averageSpeedKmh: number;
}

export interface ResultFlags {
  readonly sleepSkippedByUser: boolean;
  readonly elevationUnavailable: boolean;
  readonly timezoneWarning: boolean;
  readonly needsRecalculation: boolean;
}

export interface ProgressionInterval {
  readonly fromProgressionMeters: number;
  readonly toProgressionMeters: number;
}

export interface CoveredRange extends ProgressionInterval {
  readonly parameterSetId: string;
  readonly searchedAtLocal: LocalDateTime;
}

/** Empreinte des paramètres ayant produit un intervalle couvert — EF-RBK-013. */
export interface SearchParameterSet {
  readonly id: string;
  readonly categories: readonly CategoryKey[];
  readonly corridorMeters: number;
  readonly skipStartMeters: number;
  readonly skipFinishMeters: number;
}

export interface SearchIntegrity {
  readonly complete: boolean;
  readonly missingChunkCount: number;
  readonly missingRanges: readonly ProgressionInterval[];
  readonly coveredRanges: readonly CoveredRange[];
  readonly parameterSets: readonly SearchParameterSet[];
  readonly osmObjectsReceived: number;
  readonly poisRejectedByFilters: number;
}

export const EMPTY_SEARCH_INTEGRITY: SearchIntegrity = {
  complete: true,
  missingChunkCount: 0,
  missingRanges: [],
  coveredRanges: [],
  parameterSets: [],
  osmObjectsReceived: 0,
  poisRejectedByFilters: 0,
};

/** Dernière progression retenue. Ne contient jamais de coordonnée (EF-MOD-004). */
export interface RaceState {
  readonly lastProgressionMeters: number;
  readonly lastComputedAtLocal: LocalDateTime;
}

export type UsageMode = 'analysis' | 'race';

export interface Results {
  readonly computedAt: LocalDateTime | null;
  readonly mode: UsageMode;
  readonly totals: Totals | null;
  /** Toutes les lignes d'étape, Départ et Arrivée compris (EF-RBK-009). */
  readonly stops: readonly Stop[];
  readonly flags: ResultFlags;
  /** Empreinte de fraîcheur de EF-CAL-005. `null` si aucun calcul. */
  readonly freshness: string | null;
  readonly searchIntegrity: SearchIntegrity;
  readonly raceState: RaceState | null;
}

export const EMPTY_RESULTS: Results = {
  computedAt: null,
  mode: 'analysis',
  totals: null,
  stops: [],
  flags: {
    sleepSkippedByUser: false,
    elevationUnavailable: false,
    timezoneWarning: false,
    needsRecalculation: false,
  },
  freshness: null,
  searchIntegrity: EMPTY_SEARCH_INTEGRITY,
  raceState: null,
};

export interface RoadbookDocument {
  /** Identifiant stable sur toute la vie du roadbook — EF-RBK-005. */
  readonly id: string;
  readonly title: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly route: RouteData;
  readonly pois: ReadonlyMap<PoiId, Poi>;
  readonly results: Results;
}

/**
 * Catégorie principale d'un POI : celle de plus petit rang parmi ses catégories
 * actuellement **visibles** au sens de EF-RBK-003. Retourne `null` lorsque
 * aucune ne l'est, auquel cas le POI n'est pas affiché.
 */
export function primaryCategory(
  poi: Poi,
  hiddenCategories: ReadonlySet<CategoryKey>,
): CategoryKey | null {
  for (const key of poi.categories) {
    if (!hiddenCategories.has(key)) return key;
  }
  return null;
}

/** Catégorie principale sans filtre, pour l'export et le détail. */
export function nominalCategory(poi: Poi): CategoryKey {
  const first = poi.categories[0];
  if (first === undefined) throw new Error(`POI sans catégorie : ${poi.id}`);
  return first;
}
